import {getAuthConfig} from '@shared/utils/authConfig'

/**
 * The screen credential of a paired shop-floor display (AB#6756).
 *
 * A display signs in with the OAuth 2.0 device authorization grant (RFC 8628):
 * it asks the auth service for a device code, shows the user code (and a QR of
 * the verification URL) until a dashboard editor approves it on
 * `/display/pair`, and polls the token endpoint meanwhile. What it receives is
 * the screen's own credential, bound to one dashboard, never a person's
 * session, so it lives under its own storage key and never in `token`.
 */

export const DISPLAY_CREDENTIAL_KEY = 'mbai-display-credential'

export const DEVICE_CODE_GRANT_TYPE = 'urn:ietf:params:oauth:grant-type:device_code'

/** Refresh this long before the access token expires, so a request never races it. */
const REFRESH_AHEAD_MS = 5 * 60 * 1000

export interface DisplayCredential {
  accessToken: string
  refreshToken: string
  /** Epoch ms. */
  expiresAt: number
  /** The screen's own OAuth client, which its refresh grant is made with. */
  clientId: string
  dashboardId: string
  screenId: string
}

export interface DeviceAuthorization {
  deviceCode: string
  userCode: string
  verificationUri: string
  verificationUriComplete: string
  /** Seconds. */
  expiresIn: number
  /** Seconds between polls. */
  interval: number
}

export type DevicePollResult =
  | {status: 'approved'; credential: DisplayCredential}
  | {status: 'pending'}
  | {status: 'slow_down'}
  | {status: 'expired'}
  | {status: 'denied'}

interface TokenResponse {
  access_token: string
  refresh_token: string
  expires_in: number
  client_id: string
  dashboard_id: string
  screen_id: string
}

/** The public client a screen starts the device flow with, before it belongs to anyone. */
export const displayClientId = (): string =>
  import.meta.env.VITE_APP_DISPLAY_CLIENT_ID || 'DISP_display'

const authUrl = (path: string): string => `${getAuthConfig().authProviderBaseUrl}/auth/api/${path}`

const postForm = (path: string, body: Record<string, string>): Promise<Response> =>
  fetch(authUrl(path), {
    method: 'POST',
    headers: {'Content-Type': 'application/x-www-form-urlencoded'},
    body: new URLSearchParams(body).toString(),
  })

const toCredential = (response: TokenResponse): DisplayCredential => ({
  accessToken: response.access_token,
  refreshToken: response.refresh_token,
  expiresAt: Date.now() + response.expires_in * 1000,
  clientId: response.client_id,
  dashboardId: String(response.dashboard_id),
  screenId: String(response.screen_id),
})

export const readDisplayCredential = (): DisplayCredential | null => {
  try {
    const raw = localStorage.getItem(DISPLAY_CREDENTIAL_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<DisplayCredential>
    if (!parsed.accessToken || !parsed.refreshToken || !parsed.dashboardId || !parsed.clientId) {
      return null
    }
    return parsed as DisplayCredential
  } catch {
    return null
  }
}

export const storeDisplayCredential = (credential: DisplayCredential): void => {
  localStorage.setItem(DISPLAY_CREDENTIAL_KEY, JSON.stringify(credential))
}

export const clearDisplayCredential = (): void => {
  localStorage.removeItem(DISPLAY_CREDENTIAL_KEY)
}

/** Starts pairing: a fresh device code and user code for the pairing screen. */
export const requestDeviceAuthorization = async (): Promise<DeviceAuthorization> => {
  const response = await postForm('device-authorization/', {client_id: displayClientId()})
  if (!response.ok) {
    throw new Error(`device authorization failed: ${response.status}`)
  }
  const data = (await response.json()) as {
    device_code: string
    user_code: string
    verification_uri: string
    verification_uri_complete?: string
    expires_in: number
    interval?: number
  }
  return {
    deviceCode: data.device_code,
    userCode: data.user_code,
    verificationUri: data.verification_uri,
    verificationUriComplete:
      data.verification_uri_complete ??
      `${data.verification_uri}?code=${encodeURIComponent(data.user_code)}`,
    expiresIn: data.expires_in,
    interval: Math.max(data.interval ?? 5, 5),
  }
}

/** One poll of the token endpoint with the device code (RFC 8628 section 3.4). */
export const pollDeviceToken = async (deviceCode: string): Promise<DevicePollResult> => {
  const response = await postForm('token/', {
    grant_type: DEVICE_CODE_GRANT_TYPE,
    device_code: deviceCode,
    client_id: displayClientId(),
  })
  if (response.ok) {
    return {status: 'approved', credential: toCredential((await response.json()) as TokenResponse)}
  }
  const error = ((await response.json().catch(() => ({}))) as {error?: string}).error
  if (error === 'authorization_pending') return {status: 'pending'}
  if (error === 'slow_down') return {status: 'slow_down'}
  if (error === 'access_denied') return {status: 'denied'}
  // expired_token, an unknown code, or anything else: the code is spent.
  return {status: 'expired'}
}

let refreshing: Promise<DisplayCredential | null> | null = null

/**
 * Trades the refresh token for a new pair. Single-flight: the widgets' requests
 * share one refresh, since each refresh rotates the refresh token and a second
 * concurrent one would present the superseded token. Returns null (and drops
 * the credential) once the screen has been revoked or its refresh token has run out.
 */
export const refreshDisplayCredential = (): Promise<DisplayCredential | null> => {
  if (refreshing) return refreshing
  const current = readDisplayCredential()
  if (!current) return Promise.resolve(null)

  refreshing = (async () => {
    try {
      const response = await postForm('token/', {
        grant_type: 'refresh_token',
        refresh_token: current.refreshToken,
        client_id: current.clientId,
      })
      if (!response.ok) {
        // A network or server hiccup keeps the credential for the next try;
        // only an explicit refusal ends the pairing.
        if (response.status === 400 || response.status === 401) {
          clearDisplayCredential()
        }
        return null
      }
      // A refresh answers only the token pair; the binding stays the pairing's.
      const data = (await response.json()) as TokenResponse
      const next: DisplayCredential = {
        ...current,
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresAt: Date.now() + data.expires_in * 1000,
      }
      storeDisplayCredential(next)
      return next
    } catch {
      return null
    } finally {
      refreshing = null
    }
  })()
  return refreshing
}

/** The access token to send, refreshed first when it is about to expire. */
export const getDisplayAccessToken = async (): Promise<string | null> => {
  const current = readDisplayCredential()
  if (!current) return null
  if (current.expiresAt - Date.now() > REFRESH_AHEAD_MS) return current.accessToken
  const refreshed = await refreshDisplayCredential()
  return refreshed?.accessToken ?? readDisplayCredential()?.accessToken ?? null
}
